import i18n from '@/i18n/config';

/**
 * Get the current locale from i18n (maps 'nl' to 'nl-NL', 'en' to 'en-GB').
 */
function getLocale(): string {
  const lang = i18n.language;
  const localeMap: Record<string, string> = {
    nl: 'nl-NL',
    en: 'en-GB',
  };
  return localeMap[lang] ?? 'nl-NL';
}

/**
 * Format a date according to the current locale.
 */
export function formatDate(
  date: Date | string | number,
  options?: Intl.DateTimeFormatOptions,
): string {
  const d = date instanceof Date ? date : new Date(date);
  return new Intl.DateTimeFormat(getLocale(), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  }).format(d);
}

/**
 * Format a date with time according to the current locale.
 */
export function formatDateTime(
  date: Date | string | number,
  options?: Intl.DateTimeFormatOptions,
): string {
  const d = date instanceof Date ? date : new Date(date);
  return new Intl.DateTimeFormat(getLocale(), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  }).format(d);
}

/**
 * Format a number according to the current locale.
 */
export function formatNumber(
  value: number,
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(getLocale(), options).format(value);
}

const FILE_SIZE_UNITS = ['B', 'KB', 'MB', 'GB'] as const;

/**
 * Format a byte count as a locale-correct, human-readable file size (e.g. "6,8 MB" in
 * nl-NL, "6.8 MB" in en-GB) — used by the DAM library grid (h11-1) for per-file size.
 */
export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${formatNumber(bytes)} ${FILE_SIZE_UNITS[0]}`;
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < FILE_SIZE_UNITS.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${formatNumber(value, { maximumFractionDigits: 1, minimumFractionDigits: value < 10 ? 1 : 0 })} ${FILE_SIZE_UNITS[unitIndex]}`;
}

const MIME_FORMAT_LABELS: Record<string, string> = {
  'image/jpeg': 'JPG',
  'image/jpg': 'JPG',
  'image/png': 'PNG',
  'image/svg+xml': 'SVG',
  'image/webp': 'WEBP',
  'image/gif': 'GIF',
  'image/tiff': 'TIFF',
  'application/pdf': 'PDF',
};

/**
 * Short, uppercase format label for a mime type (e.g. "image/png" -> "PNG"), used in the
 * product-media tile metadata line (h2-4 mockup product-media.html `.m-meta`, e.g.
 * "2400×2400 · PNG · 2,1 MB"). Returns `null` for an unknown or missing mime type rather
 * than guessing — callers omit the segment instead of showing a wrong/empty label.
 */
export function formatMediaFormat(mimeType: string | null | undefined): string | null {
  if (mimeType == null) return null;
  return MIME_FORMAT_LABELS[mimeType.toLowerCase()] ?? null;
}

/**
 * Format a percentage according to the current locale.
 */
export function formatPercentage(
  value: number,
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(getLocale(), {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
    ...options,
  }).format(value / 100);
}
